import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import {
  AppPageHeader,
  ColumnsType,
  InfiniteTable,
} from '@/components/_common';
import { WorkorderItem, useWorkorderItems } from '.';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { Alert, Button, Divider } from 'antd';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { useWorkOrderItemsVerify } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { useDebounce } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useWorkOrderKitList } from '@/core/domain/workorder/Kits/Kits.hooks';
import {
  WorkOrderKit,
  WorkOrderKitListRequest,
} from '@/core/domain/workorder/Kits/Kits.types';
import WorkOrderKitListExtraFilter from '@/components/_common/WorkOrderKitListExtraFilter';
import { isAppPortocar } from '@/index';
import { PortoApoliceType } from '@/core/domain/porto-seguro/apolices/apolices.types';

interface Props {
  onCancel: VoidFunction;
}

function SelectKitList({ onCancel }: Props) {
  const intl = useIntl();
  const { items, setItems, onNextStep, onPrevStep } = useWorkorderItems();
  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const [queryParams, setQueryParams] = useState<WorkOrderKitListRequest>({
    companyId: workorder?.company.id,
  });

  const { data: workorderItemsData } = useWorkOrderItemsVerify(complement?.id);
  const workorderItems = workorderItemsData?.data || [];

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } = useWorkOrderKitList({
    ...queryDebounce,
    companyId: workorder?.company.id,
  });

  const kits = flatInfiniteData(data);

  const columns: ColumnsType<WorkOrderKit> = [
    {
      key: 'description',
      dataIndex: 'description',
      title: intl.formatMessage({ id: 'tasks.general.form.description' }),
      ellipsis: true,
      sorter: true,
    },
  ];

  const hasItemsRegistred = useMemo(() => {
    return items.some((item) =>
      workorderItems.some(
        (registredItem) => item.itemId === registredItem.itemId,
      ),
    );
  }, [workorderItems, items]);

  const getMinDiscountPolicy = useCallback(
    (type: WorkOrderItemType) => {
      if (
        isAppPortocar &&
        type === WorkOrderItemType.Service &&
        !workorder?.partnerDiscount &&
        workorder?.employeeDiscount
      )
        return 10;

      if (
        !isAppPortocar ||
        !complement?.policyNumber ||
        type !== WorkOrderItemType.Service ||
        !workorder?.partnerDiscount
      )
        return undefined;

      const isPortoPolicy =
        !!complement?.policyNumber &&
        complement?.companyCode?.toUpperCase() === PortoApoliceType.PORTO;

      return isPortoPolicy ? 20 : 15;
    },
    [
      isAppPortocar,
      complement?.policyNumber,
      complement?.companyCode,
      workorder?.partnerDiscount,
    ],
  );

  return (
    <>
      <AppPageHeader
        searchValue={queryParams?.description}
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, description: value }))
        }
        extraFilters={
          <WorkOrderKitListExtraFilter
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <Divider />

      <InfiniteTable
        loading={isFetching}
        rowKey={(row) => row.id}
        dataSource={kits}
        columns={columns}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        rowSelection={{
          preserveSelectedRowKeys: true,
          type: 'checkbox',
          onChange: (_, rows) => {
            if (!complement || !workorder) return;

            const kitItems = rows.flatMap((kit) => {
              const type = {
                0: WorkOrderItemType.Service,
                1: WorkOrderItemType.Part,
              };

              return kit.items.map<WorkorderItem>((item) => {
                const discount = getMinDiscountPolicy(type[item.item.type]);

                const prices = item.prices?.find(
                  (discount) => discount.companyId === workorder.company.id,
                );
                const discounts = item.discounts?.find(
                  (discount) => discount.company.id === workorder.company.id,
                );

                const increaseDecrease = discounts?.increaseDecrease || 0;
                const price = prices?.price || 0;

                const unitPrice = price * (increaseDecrease / 100) + price;

                const discountValue =
                  typeof discount === 'number'
                    ? ((item.quantity || 0) *
                        (unitPrice || 0) *
                        (discount || 0)) /
                      100
                    : undefined;

                return {
                  complementId: complement?.id,
                  kitId: kit.id,
                  kitName: kit.description,
                  itemId: item?.item?.itemId,
                  itemName: item.item?.name,
                  itemType: type[item.item?.type],
                  subAreaId: item.item?.subAreaId,
                  quantity: item.quantity,
                  manufacturerId: item.item?.manufacturerId,
                  manufacturingCode: item.item?.manufacturingCode,
                  unitPrice,
                  unitCost: prices?.cost || 0,
                  discount: discount || 0,
                  discountValue,
                  isInternal: true,
                };
              });
            });

            setItems(kitItems);
          },
          selectedRowKeys: items
            .filter((item) => !!item.kitId)
            .map((item) => item.kitId as number)
            .reduce(
              (acc, id) => (!acc.includes(id) ? [...acc, id] : acc),
              [] as number[],
            ),
          getCheckboxProps: (item) => ({
            disabled: !!workorderItems.find(
              (workorderItem) => item.id === workorderItem.kitId,
            ),
          }),
        }}
      />

      {hasItemsRegistred && (
        <Alert
          style={{ marginTop: 24 }}
          type="warning"
          description="
        O kit selecionado contem itens já cadastrados no orçamento, ao
        vincular o kit o item já cadastrado será substituido pelo novo."
        />
      )}

      <SpaceForButton justifyContent="end" style={{ marginTop: 24 }}>
        <Button block onClick={onCancel}>
          Cancelar
        </Button>

        <Button block onClick={() => onPrevStep()}>
          Voltar
        </Button>

        <Button
          block
          type="primary"
          onClick={onNextStep}
          disabled={!items.length}
        >
          Próximo
        </Button>
      </SpaceForButton>
    </>
  );
}

export default SelectKitList;
